'use client';

import type { Collaborator, DisableInput } from '@/app/models/collaborator';
import { useEnableAllMutation, useSearchPersonQuery } from '@/app/services/personApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import ConsiderCollaboratorTable from '@/components/considerCollaborator/ConsiderCollaboratorTable';
import NewConsiderCollaborator from '@/components/considerCollaborator/NewConsiderCollaborator';
import PeriodDetails from '@/components/periodDetails/PeriodDetails';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function ConsiderCollaborator() {
  const theme = useTheme();
  const { t } = useTranslation('considerCollaborator');
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [selectedCollaborators, setSelectedCollaborators] = useState<number[]>([]);

  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaborators, isFetching: isLoadingCollaborators } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: false,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const [enableAll] = useEnableAllMutation();

  const handleSubmit = async () => {
    if (!selectedCollaborators || selectedCollaborators.length < 1) return;
    toast.info(t('reconsider.sent'));
    const requestBody: DisableInput = {
      ids: selectedCollaborators,
    };

    await enableAll(requestBody);
    setSelectedCollaborators([]);
    toast.success(t('reconsider.added'));
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PeriodDetails />
      <PageTitle
        title={t('consider.collaborator')}
        titleLabel={` (${t('sharing')})`}
        subtitle={`${t('consider.subtitle')}`}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'end',
        }}
      >
        <Box sx={{ flex: 1, minWidth: '250px' }}>
          <Typography component="p" variant="fieldHeader">
            {t('collaborator')}
          </Typography>
          <GenericAutoComplete
            value={collaborator}
            onChange={(value) => {
              setCollaborator(value);
            }}
            equalityTest={(option, value) => option?.email === value?.email}
            getOptionLabel={(option) => `${option?.badge} - ${option?.name}`}
            onSearch={(value) => {
              setCollaboratorSearch(value);
            }}
            options={collaboratorSearch !== '' && collaborators?.content ? collaborators?.content : []}
            placeholder={t('search.collaborator') || ''}
            loading={isLoadingCollaborators}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Button
            variant="primary"
            disabled={selectedCollaborators.length < 1}
            onClick={() => {
              handleSubmit();
            }}
            sx={{
              textTransform: 'none',
            }}
          >
            {t('consider')}
          </Button>
          <TextButton
            text={`+ ${t('remove.from.sharing')}`}
            onClick={() => {
              setDrawerStatus(true);
            }}
          />
        </Box>
      </Box>

      <ConsiderCollaboratorTable
        selectedCollaborators={selectedCollaborators}
        setSelectedCollaborators={setSelectedCollaborators}
        searchTerm={collaborator?.name || ''}
      />

      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewConsiderCollaborator setDrawerOpen={setDrawerStatus} />
      </LateralDrawer>
    </Box>
  );
}

export default ConsiderCollaborator;
